import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Select, Space, message, Switch, Tag, Modal, Form, Popconfirm, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall, makeDeleteCall } from '../../utils/helper';
import './masters-config.css';

const { Option } = Select;

const ItemCodes = () => {
  const [itemsList, setItemsList] = useState({ data: [], loading: false, error: null });
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [form] = Form.useForm();

  const fetchItems = useCallback(async () => {
    setItemsList(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await makeGetCall('/items?all=true');
      if (response?.data?.success) {
        setItemsList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch items');
      }
    } catch (error) {
      setItemsList({ data: [], loading: false, error: error.message || 'Failed to fetch items' });
      message.error('Failed to fetch items');
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      // Only active groups are selectable as an item's routing target.
      const response = await makeGetCall('/modality-groups');
      if (response?.data?.success) {
        setGroupOptions(response.data.data || []);
      }
    } catch (error) {
      // Non-fatal: items can still be managed without groups.
      console.error('Failed to fetch modality groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchGroups();
  }, [fetchItems, fetchGroups]);

  // id -> {name, modalities} lookup for rendering the Modality Group column.
  const groupById = useMemo(() => {
    const map = {};
    (groupOptions || []).forEach(g => { map[g.id] = g; });
    return map;
  }, [groupOptions]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      item_code: record.item_code,
      item_name: record.item_name,
      modality_group_id: record.modality_group_id || undefined,
      his_billing_code: record.his_billing_code,
      is_active: record.active,
    });
    setModalVisible(true);
  };

  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      const res = await makePostCall('/item', {
        item_id: record.id,
        is_active: checked,
      });
      if (res?.data?.success) {
        message.success(`Item ${checked ? 'activated' : 'deactivated'}`);
        fetchItems();
      } else {
        throw new Error(res?.data?.message || 'Failed to update item');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update item');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id);
    try {
      const res = await makeDeleteCall(`/item/${record.id}`);
      if (res?.data?.success) {
        message.success('Item deleted successfully');
        fetchItems();
      } else {
        throw new Error(res?.data?.message || 'Failed to delete item');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...(editingRecord ? { item_id: editingRecord.id } : {}),
        item_code: values.item_code?.trim(),
        item_name: values.item_name?.trim(),
        modality_group_id: values.modality_group_id ?? null,
        his_billing_code: values.his_billing_code?.trim() || '',
        is_active: values.is_active,
      };

      const res = await makePostCall('/item', payload);
      if (res?.data?.success) {
        message.success(`Item ${editingRecord ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchItems();
      } else {
        throw new Error(res?.data?.message || 'Failed to save item');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const filteredItems = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (itemsList.data || []).filter(item => {
      if (statusFilter === 'active' && !item.active) return false;
      if (statusFilter === 'inactive' && item.active) return false;
      if (search) {
        const haystack = `${item.item_code} ${item.item_name} ${item.his_billing_code} ${item.modality_group_name}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [itemsList.data, statusFilter, searchText]);

  const columns = [
    {
      title: 'Item Code',
      dataIndex: 'item_code',
      key: 'item_code',
      sorter: (a, b) => (a.item_code || '').localeCompare(b.item_code || ''),
    },
    {
      title: 'Name',
      dataIndex: 'item_name',
      key: 'item_name',
      sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
    },
    {
      title: 'Modality Group',
      dataIndex: 'modality_group_id',
      key: 'modality_group_id',
      render: (gid, record) => {
        if (!gid) return <span style={{ color: '#94a3b8' }}>—</span>;
        const grp = groupById[gid];
        const name = record.modality_group_name || grp?.label || `#${gid}`;
        const codes = (grp?.modalities) || record.modalities || [];
        return (
          <span>
            <Tag color="blue">{name}</Tag>
            {codes.length > 0 && (
              <span style={{ color: '#64748b', fontSize: 12 }}>{codes.join(', ')}</span>
            )}
          </span>
        );
      },
    },
    {
      title: 'HIS Code',
      dataIndex: 'his_billing_code',
      key: 'his_billing_code',
      render: (code) => code || <span style={{ color: '#94a3b8' }}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={active}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Item">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete this item?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />} loading={deletingId === record.id}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Item Codes</span>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Item
        </Button>
      </div>

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by code, name, HIS code or group"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 340 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {itemsList.error && (
        <div className="mc-error">
          <p>{itemsList.error}</p>
          <Button onClick={fetchItems}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Item Codes</span>
          <span className="mc-count">{filteredItems.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredItems}
            loading={itemsList.loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit Item' : 'Add Item'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Item Code"
            name="item_code"
            tooltip="Internal PACS procedure code. Must be unique."
            rules={[
              { required: true, message: 'Please enter the item code' },
              { max: 50, message: 'Code must be 50 characters or less' },
            ]}
          >
            <Input placeholder="e.g. CT-BRAIN-PLAIN" />
          </Form.Item>

          <Form.Item
            label="Item Name"
            name="item_name"
            tooltip="Friendly procedure name shown in the worklist."
            rules={[
              { required: true, message: 'Please enter the item name' },
              { max: 200, message: 'Name must be 200 characters or less' },
            ]}
          >
            <Input placeholder="e.g. CT Brain Plain" />
          </Form.Item>

          <Form.Item
            label="Modality Group"
            name="modality_group_id"
            tooltip="The single group this exam routes to. The order's modality column is derived from this group's modalities."
          >
            <Select
              placeholder="Select a modality group"
              options={groupOptions}
              loading={groupsLoading}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            label="HIS Billing Code"
            name="his_billing_code"
            tooltip="The hospital HIS's own code for this procedure. Used to match incoming HL7 orders. Optional, unique when set."
            rules={[{ max: 100, message: 'HIS code must be 100 characters or less' }]}
          >
            <Input placeholder="e.g. RAD-101" />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ItemCodes;
